import {
  Box,
  IconButton,
  InputAdornment,
  Modal,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
  LoginFormBox,
  LoginMainBox,
  LoginSigUpTextLink,
  styles,
} from "./loginAccountStyles";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import { useDispatch, useSelector } from "react-redux";
import {
  chooseModalResetPass,
  chooseModalSignUp,
  closeChooseModal,
  setEmail,
} from "../../../../Infrastructure/States/authModalsSlice";
import { useFormik } from "formik";
import * as Yup from "yup";
import { signInWithEmailAndPassword } from "firebase/auth";
import { ShowErrorToast } from "../../Toast/toast";
import { get, ref, update } from "firebase/database";
import { trackEvent } from "../../../../Infrastructure/Utilities/analytics";
import ButtonLoading from "../../Button/ButtonLoading";
import AnalyticsConstants from "../../../../Infrastructure/Constants/AnalyticsContants";
import { useNavigate } from "react-router-dom";
import ERROR_CONSTANTS from "../../../../Infrastructure/Constants/errorConstants";
import { auth, database } from "../../../../Infrastructure/config";
import { emailSchema } from "../../../../Infrastructure/Utilities/emailValidator";
import { fetchUserData } from "../../../../Infrastructure/States/userDataSlice";
import * as amplitude from "@amplitude/analytics-browser";
import paths from "../../../../Infrastructure/Router/paths";
import { UNAUTH_LOGS_CLIENT } from "../../../../Infrastructure/Constants/logsConstant";
import { safeClientLogEvent } from "../../../../Infrastructure/Utilities/safeClientLogEvent";
import { StyledTextField } from "../../../../Infrastructure/Theme/styledComponents";

export const LoginAccount = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false)
  const email = useSelector((state) => state.auth.email);
  const isOpenModal = useSelector((state) => state.auth.isOpenModal);
  const [showPassword, setShowPassword] = useState(true);

  const handleCloseModal = () => {
    dispatch(closeChooseModal());
    loginFormik.resetForm();
  };
  const handleSignUpButtonClick = () => {
    dispatch(chooseModalSignUp());
    loginFormik.resetForm();
  };
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const handleResetPassButtonClick = () => {
    dispatch(chooseModalResetPass());
  };
  const handleAuthenticationError = (error) => {
    if (!error) {
      return;
    }
    let errorMessage = ERROR_CONSTANTS.GENERIC_ERROR_TOAST;
    switch (error.code) {
      case "auth/invalid-login-credentials":
        errorMessage =
          "Invalid credentials. Please check your email or password and try again.";
        break;
      case "auth/wrong-password":
        errorMessage =
          "Invalid password. Please check your password and try again.";
        break;
      case "auth/user-not-found":
        errorMessage = "User not found! Please Sign Up.";
        break;
      case "auth/too-many-requests":
        errorMessage =
          error?.error?.message || "Too many requests. Please try again later.";
        break;
      default:
        ShowErrorToast(error);
    }
    ShowErrorToast(errorMessage);
  };
  const loginFormik = useFormik({
    initialValues: {
      email: email || "",
      password: "",
    },
    validationSchema: Yup.object({
      email: emailSchema,
      password: Yup.string()
        .min(6, "Must be 6 characters")
        .required("Password"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true)
        const { email, password } = values;
        const restrictedEmails = process.env.REACT_APP_ADMIN_EMAIL?.split(',').map(e => e.trim()) || [];
        if (restrictedEmails.includes(email)) {
          ShowErrorToast("Access denied. Admin is not authorized to log in.");
          setLoading(false);
          return;
        }
        const userCredential = await signInWithEmailAndPassword(
          auth,
          email,
          password
        );
        handleCloseModal()
        const user = userCredential.user;
        try {
          const userRef = ref(database, `users/${user.uid}`);
          await update(userRef, { email_verified: user.emailVerified });
          const snapshot = await get(userRef);
          const userData = snapshot.val();
          const eventType = userData?.is_educator ? AnalyticsConstants.EDUCATOR_LOGIN_SUCCESS : AnalyticsConstants.LEARNER_LOGIN_SUCCESS;
          amplitude.setUserId(user.uid);
          const identifyObj = new amplitude.Identify().set("email", userData?.account?.email);
          amplitude.identify(identifyObj)
          dispatch(fetchUserData(user.uid))
          // Track login events (if it fails, log but continue)
          try {
            trackEvent(eventType, {
              user_id: user.uid,
              email: user.email,
            });
          } catch (trackingError) {
            safeClientLogEvent({
              uid: user?.uid,
              ...UNAUTH_LOGS_CLIENT.TRACKING_EVENT_FAILED,
              source: "loginAccount.jsx",
              error: trackingError.message,
              metadata: loginFormik.values.email
            });
          }
        } catch (firebaseError) {
          safeClientLogEvent({
              uid: user?.uid,
              ...UNAUTH_LOGS_CLIENT.UPDATING_USER_DATA_FAILED,
              source: "loginAccount.jsx",
              error: firebaseError.message,
              metadata: loginFormik.values.email
            })
        }
        dispatch(setEmail(""))
        if (window.location.pathname === paths.home.path && user.emailVerified === true) {
          navigate(paths.dashboardPage.path)
        }
      } catch (error) {
        safeClientLogEvent({
              ...UNAUTH_LOGS_CLIENT.LOGIN_FAILED,
              source: "loginAccount.jsx",
              error: error.message,
              metadata: {email: values.email}
            })
        handleAuthenticationError(error);
      } finally {
        setLoading(false)
      }
    },
  });
  const handleEmailChange = (event) => {
    const { value } = event.target;
    dispatch(setEmail(value))
    loginFormik.setFieldValue("email", value)
  };
  return (
    <Box>
      {/* Log in Modal */}
      <Modal
        open={isOpenModal}
        disableScrollLock
        onClose={handleCloseModal}
        aria-labelledby="login-modal-title"
        aria-describedby="login-modal-description"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        BackdropProps={{
          sx: styles.backdrop,
        }}
      >
        <LoginMainBox>
          <Box
            pb={1}
            pt={1}
            onClick={handleCloseModal}
            sx={{
              position: "relative",
              top: "9px",
              right: "-142px",
              cursor:'pointer'
            }}
          >
            <CloseIcon/>
          </Box>
          <Typography
            variant="h1"
            px={4}
            mb={4}
            color="primary"
            sx={{ fontSize: "36px" }}
          >
            Log In
          </Typography>
          <Box>
            <LoginSigUpTextLink
              onClick={handleSignUpButtonClick}
              variant="h6"
              mb={3}
              sx={{ fontSize: "16px" }}
            >
              New? Create An Account.
            </LoginSigUpTextLink>
          </Box>
          <LoginFormBox
            component="form"
            onSubmit={loginFormik.handleSubmit}
            noValidate
          >
            <StyledTextField
              name="email"
              label={
                loginFormik.touched.email && Boolean(loginFormik.errors.email)
                  ? loginFormik.errors.email
                  : "Email"
              }
              variant="standard"
              type="email"
              autoComplete="username"
              onChange={handleEmailChange}
              value={loginFormik.values.email}
              error={
                loginFormik.touched.email && Boolean(loginFormik.errors.email)
              }
              sx={{ pb: "14px", pt: "5" }}
              fullWidth
            />

            <StyledTextField
              name="password"
              label={
                loginFormik.touched.password &&
                  Boolean(loginFormik.errors.password)
                  ? loginFormik.errors.password
                  : "Password"
              }
              variant="standard"
              type={showPassword ? "password" : "text"}
              onChange={loginFormik.handleChange}
              value={loginFormik.values.password}
              autoComplete="current-password"
              error={
                loginFormik.touched.password &&
                Boolean(loginFormik.errors.password)
              }
              sx={{ pb: "14px", pt: "5" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleTogglePasswordVisibility}>
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
            <Box pb={2} width={'100%'}>
              <Typography
                variant="body2"
                color={"primary"}
                onClick={handleResetPassButtonClick}
                sx={{
                  textDecoration: "none",
                  fontSize: "16px",
                  cursor: "pointer",
                }}
              >
                Forgot password?
              </Typography>
            </Box>
            <ButtonLoading text={"Log In"} type={"submit"} loading={loading} />
          </LoginFormBox>
        </LoginMainBox>
      </Modal>
    </Box>
  );
};
